import React, { useState, useEffect } from 'react';
import {
    Create,
    SimpleForm,
    SelectInput,
    required,
    useTranslate,
    useGetList,
    AutocompleteArrayInput,
    useNotify,
    useRedirect,
    FormDataConsumer,
    useCreate,
    useDataProvider,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box, Typography, Alert, Card, CardContent, IconButton, Button, ButtonGroup, Select, MenuItem, Checkbox, ListItemText, FormControl, InputLabel, FormHelperText, Chip, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Devices, Memory, ArrowBack, CheckBox, CheckBoxOutlineBlank, ExpandMore, Search, Clear, Warning } from '@mui/icons-material';
import { tokenManager } from '../../utils/tokenManager';
import { API_CONFIG } from '../../config/api';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const FIRMWARE_UPDATE_KNOWN_FIELDS = new Set([
    'site_id', 'firmware_id', 'target_type', 'target_ids',
    'vendor', 'model', 'apply_at', 'description', 'scheduled_start',
]);

const mapFirmwareUpdateBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const firmwareUpdateFieldLabelKey = (field: string): string =>
    FIRMWARE_UPDATE_KNOWN_FIELDS.has(field)
        ? `resources.firmware-updates.fields.${field}`
        : field;

// Version comparison function for semantic versioning
const compareVersions = (v1: string, v2: string): number => {
    const v1Parts = v1.split('.').map(Number);
    const v2Parts = v2.split('.').map(Number);
    const maxLength = Math.max(v1Parts.length, v2Parts.length);

    for (let i = 0; i < maxLength; i++) {
        const num1 = v1Parts[i] || 0;
        const num2 = v2Parts[i] || 0;

        if (num1 > num2) return 1;  // v1 is greater
        if (num1 < num2) return -1; // v2 is greater
    }

    return 0; // versions are equal
};

const CreateToolbar = ({ onSaveClick }: { onSaveClick: (e: React.MouseEvent) => void }) => {
    const translate = useTranslate();
    return (
    <Box
        sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100vw',
            marginLeft: 'calc(-50vw + 50%)',
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            mt: { xs: 2, sm: 4 },
            zIndex: 1000,
        }}
    >
        <Box sx={{ maxWidth: 800, margin: '0 auto', px: { xs: 2, sm: 3 }, py: 2 }}>
            <Button
                variant="contained"
                onClick={onSaveClick}
                type="button"
                sx={{
                    backgroundColor: '#00C853',
                    color: 'white',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            >
                {translate('resources.firmware-updates.toolbar.save')}
            </Button>
        </Box>
    </Box>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/firmware-updates')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.firmware-updates.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.firmware-updates.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const FirmwareUpdatesCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [sites, setSites] = useState<any[]>([]);
    const [loadingSites, setLoadingSites] = useState(true);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [downgrades, setDowngrades] = useState<Array<{ charger: string; current: string; target: string }>>([]);
    const [pendingFormData, setPendingFormData] = useState<any>(null);
    const [showBlockerModal, setShowBlockerModal] = useState(false);
    const [blockerErrors, setBlockerErrors] = useState<Record<string, string[]>>({});
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    // Fetch sites dropdown (lightweight)
    useEffect(() => {
        const fetchSites = async () => {
            try {
                const token = await tokenManager.getAccessToken();
                const response = await fetch(`${API_CONFIG.baseURL}/api/backend/sites/dropdown`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setSites(data);
                }
            } catch (error) {
                console.error('Failed to load sites:', error);
                notify(translate('resources.firmware-updates.notifications.failed_load_sites'), { type: 'error' });
            } finally {
                setLoadingSites(false);
            }
        };
        fetchSites();
    }, [notify]);

    // Fetch all firmware for dropdown
    const { data: allFirmware, isLoading: firmwareLoading } = useGetList(
        'charger-firmware',
        {
            pagination: { page: 1, perPage: 500 },
            sort: { field: 'version', order: 'DESC' },
            filter: { is_active: true },
        }
    );

    const [create] = useCreate();

    // Function to check if any chargers would be downgraded
    const checkVersionCompatibility = async (formData: any) => {
        try {
            const firmwareId = formData.firmware_id;
            const chargerIds = formData.target_ids;

            console.log('[Version Check] Starting check with:', { firmwareId, chargerIds });

            if (!firmwareId || !chargerIds || chargerIds.length === 0) {
                console.log('[Version Check] Missing firmware_id or target_ids');
                return { hasIssues: false, downgrades: [] };
            }

            // Get selected firmware details
            const selectedFirmware = allFirmware?.find((f: any) => f.id === firmwareId);
            console.log('[Version Check] Selected firmware:', selectedFirmware);

            if (!selectedFirmware) {
                console.log('[Version Check] Firmware not found in list');
                return { hasIssues: false, downgrades: [] };
            }

            const targetVersion = selectedFirmware.version;
            console.log('[Version Check] Target version:', targetVersion);

            // Fetch charger details including current firmware versions
            const token = await tokenManager.getAccessToken();
            const chargerPromises = chargerIds.map((id: string) =>
                fetch(`${API_CONFIG.baseURL}/api/backend/chargers/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                }).then(res => res.json())
            );

            const chargers = await Promise.all(chargerPromises);
            console.log('[Version Check] Fetched chargers:', chargers);

            const downgrades: Array<{ charger: string; current: string; target: string }> = [];

            chargers.forEach((response: any) => {
                // Handle wrapped response - API returns { data: {...} }
                const charger = response.data || response;

                console.log('[Version Check] Checking charger:', {
                    name: charger.name,
                    firmwareVersion: charger.firmwareVersion,
                    hasVersion: !!charger.firmwareVersion
                });

                if (charger.firmwareVersion) {
                    const comparison = compareVersions(targetVersion, charger.firmwareVersion);
                    console.log('[Version Check] Version comparison:', {
                        target: targetVersion,
                        current: charger.firmwareVersion,
                        result: comparison,
                        isDowngrade: comparison <= 0
                    });

                    // If target version <= current version
                    if (comparison <= 0) {
                        downgrades.push({
                            charger: charger.name || charger.id,
                            current: charger.firmwareVersion,
                            target: targetVersion,
                        });
                    }
                }
            });

            console.log('[Version Check] Final result:', { hasIssues: downgrades.length > 0, downgrades });
            return { hasIssues: downgrades.length > 0, downgrades };
        } catch (error) {
            console.error('[Version Check] Error:', error);
            return { hasIssues: false, downgrades: [] };
        }
    };

    // Handle save button click
    const handleSaveClick = async (formData: any) => {
        console.log('[Save Click] handleSaveClick called with formData:', formData);

        const { hasIssues, downgrades: versionIssues } = await checkVersionCompatibility(formData);

        console.log('[Save Click] Version check complete:', { hasIssues, downgrades: versionIssues });

        if (hasIssues) {
            console.log('[Save Click] Showing warning modal');
            setDowngrades(versionIssues);
            setPendingFormData(formData);
            setShowWarningModal(true);
        } else {
            console.log('[Save Click] No issues, proceeding with save');
            // No issues, proceed with save
            submitForm(formData);
        }
    };

    // Submit the form
    const submitForm = (formData: any) => {
        const transformedData = {
            ...formData,
            target_type: 'charger',
        };

        create(
            'firmware-updates',
            { data: transformedData },
            {
                onSuccess: (data: any) => {
                    setServerErrors({});
                    notify('resources.firmware-updates.notifications.created', { type: 'success' });
                    redirect('show', 'firmware-updates', data.id);
                },
                onError: (error: any) => {
                    console.log('[Submit Error] Full error object:', error);
                    console.log('[Submit Error] error.body:', error.body);

                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const errorCode = apiError?.errorCode;

                    // CHARGERS_NOT_READY uses a charger-name-keyed errors map —
                    // surface it via the blocker modal instead of field-level binding.
                    if (errorCode === 'CHARGERS_NOT_READY' && rawErrors && Object.keys(rawErrors).length > 0) {
                        const blockerMap: Record<string, string[]> = {};
                        for (const [k, v] of Object.entries(rawErrors)) {
                            blockerMap[k] = Array.isArray(v) ? (v as string[]) : [String(v)];
                        }
                        setBlockerErrors(blockerMap);
                        setShowBlockerModal(true);
                        return;
                    }

                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapFirmwareUpdateBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(firmwareUpdateFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = errorCode
                        ? `errors.${errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }
        );
    };

    // Handle confirm from warning modal
    const handleConfirmDowngrade = () => {
        setShowWarningModal(false);
        if (pendingFormData) {
            submitForm(pendingFormData);
        }
    };

    // Handle cancel from warning modal
    const handleCancelDowngrade = () => {
        setShowWarningModal(false);
        setPendingFormData(null);
        setDowngrades([]);
    };

    return (
        <>
            <Create
                title={false}
                component="div"
                redirect={false}
                sx={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    backgroundColor: 'transparent',
                    '& .RaCreate-main': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                }}
            >
                <SimpleForm
                    toolbar={false}
                    onSubmit={handleSaveClick}
                    sx={{
                        '& .MuiPaper-root': {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                        },
                        '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                            px: { xs: 0, sm: 2 },
                            py: { xs: 1, sm: 2 },
                        },
                        padding: { xs: 0, sm: 'inherit' },
                    }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {/* Target Selection Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Devices sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.firmware-updates.sections.target_selection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Step 1: Select Site */}
                                <SelectInput
                                    source="site_id"
                                    label={translate('resources.firmware-updates.fields.site')}
                                    helperText={translate('resources.firmware-updates.helpers.site')}
                                    choices={sites}
                                    optionText="name"
                                    optionValue="id"
                                    validate={required()}
                                    disabled={loadingSites}
                                    fullWidth
                                />

                                <FormDataConsumer>
                                    {({ formData }) => (
                                        <SiteBasedSelection
                                            formData={formData}
                                            translate={translate}
                                            notify={notify}
                                        />
                                    )}
                                </FormDataConsumer>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Firmware Selection Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Memory sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.firmware-updates.sections.firmware_selection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <FormDataConsumer>
                                    {({ formData }) => {
                                        if (!formData.vendor || !formData.model) {
                                            return (
                                                <Alert severity="info">
                                                    {translate('resources.firmware-updates.helpers.select_vendor_model_first')}
                                                </Alert>
                                            );
                                        }

                                        // Filter firmware by vendor and model, sorted by version DESC
                                        const filteredFirmware = (allFirmware || [])
                                            .filter((f: any) => f.vendor === formData.vendor && f.model === formData.model)
                                            .sort((a: any, b: any) => {
                                                return b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' });
                                            });

                                        return (
                                            <>
                                                <SelectInput
                                                    source="firmware_id"
                                                    label={translate('resources.firmware-updates.fields.firmware_id')}
                                                    helperText={`Select firmware version (${filteredFirmware.length} available for ${formData.vendor} ${formData.model})`}
                                                    choices={filteredFirmware}
                                                    optionText={(record: any) => `v${record.version}${record.description ? ` - ${record.description}` : ''}`}
                                                    optionValue="id"
                                                    validate={required()}
                                                    disabled={filteredFirmware.length === 0 || firmwareLoading}
                                                    fullWidth
                                                />

                                                {filteredFirmware.length === 0 && (
                                                    <Alert severity="warning">
                                                        {translate('resources.firmware-updates.helpers.no_firmware_available', { vendor: formData.vendor, model: formData.model })}
                                                    </Alert>
                                                )}

                                                <Alert severity="info">
                                                    {translate('resources.firmware-updates.warnings.immediate_update')}
                                                </Alert>
                                            </>
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                <FormDataConsumer>
                    {({ formData }) => (
                        <CreateToolbar onSaveClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('[TOOLBAR CLICK] Button clicked, formData:', formData);
                            handleSaveClick(formData);
                        }} />
                    )}
                </FormDataConsumer>
            </SimpleForm>
        </Create>

        {/* Warning Modal for Version Downgrade */}
        <Dialog
            open={showWarningModal}
            onClose={handleCancelDowngrade}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#F57C00' }}>
                <Warning />
                <Typography variant="h6" component="span">
                    {translate('resources.firmware-updates.modals.version_warning.title')}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {translate('resources.firmware-updates.modals.version_warning.message')}
                </DialogContentText>

                <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 2 }}>
                    {downgrades.map((item, index) => (
                        <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.charger}
                            </Typography>
                            <Typography variant="body2">
                                {translate('resources.firmware-updates.modals.version_warning.current_version')}: <strong>v{item.current}</strong> → {translate('resources.firmware-updates.modals.version_warning.target_version')}: <strong>v{item.target}</strong>
                            </Typography>
                        </Alert>
                    ))}
                </Box>

                <Alert severity="error">
                    <Typography variant="body2">
                        {translate('resources.firmware-updates.modals.version_warning.warning_text')}
                    </Typography>
                </Alert>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={handleCancelDowngrade}
                    variant="outlined"
                    color="inherit"
                >
                    {translate('resources.firmware-updates.modals.version_warning.cancel')}
                </Button>
                <Button
                    onClick={handleConfirmDowngrade}
                    variant="contained"
                    color="warning"
                    startIcon={<Warning />}
                >
                    {translate('resources.firmware-updates.modals.version_warning.proceed')}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Blocker Modal for Chargers Not Ready */}
        <Dialog
            open={showBlockerModal}
            onClose={() => setShowBlockerModal(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#D32F2F' }}>
                <Warning />
                <Typography variant="h6" component="span">
                    {translate('resources.firmware-updates.modals.blocker.title')}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {translate('resources.firmware-updates.modals.blocker.message')}
                </DialogContentText>

                <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 2 }}>
                    {Object.entries(blockerErrors).map(([chargerName, reasons], index) => (
                        <Alert key={index} severity="error" sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {chargerName}
                            </Typography>
                            {reasons.map((reason, reasonIndex) => (
                                <Typography key={reasonIndex} variant="body2">
                                    {reason}
                                </Typography>
                            ))}
                        </Alert>
                    ))}
                </Box>

                <Alert severity="info">
                    <Typography variant="body2">
                        {translate('resources.firmware-updates.modals.blocker.info')}
                    </Typography>
                </Alert>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={() => setShowBlockerModal(false)}
                    variant="contained"
                    color="primary"
                >
                    {translate('resources.firmware-updates.modals.blocker.ok')}
                </Button>
            </DialogActions>
        </Dialog>
    </>
    );
};

// Component for charger selection with checkboxes and Select All/Deselect All inside dropdown
const ChargerSelectionWithSelectAll = ({ chargers, loading, translate }: any) => {
    const { watch, setValue } = useFormContext();
    const [error, setError] = React.useState<string>('');
    const [searchQuery, setSearchQuery] = React.useState<string>('');

    // Watch the current selection
    const selectedIds = watch('target_ids') || [];

    // Filter chargers based on search query
    const filteredChargers = React.useMemo(() => {
        if (!searchQuery.trim()) return chargers;

        const query = searchQuery.toLowerCase();
        return chargers.filter((charger: any) => {
            const serialNumber = (charger.serial_number || '').toLowerCase();
            const name = (charger.name || '').toLowerCase();
            const id = (charger.id || '').toLowerCase();
            return serialNumber.includes(query) || name.includes(query) || id.includes(query);
        });
    }, [chargers, searchQuery]);

    const handleChange = (event: any) => {
        const value = event.target.value;
        setValue('target_ids', value, { shouldValidate: true, shouldDirty: true });

        // Clear error when user selects something
        if (value.length > 0) {
            setError('');
        }
    };

    const handleSelectAll = (event: React.MouseEvent) => {
        event.stopPropagation();
        // Select all filtered chargers
        const filteredIds = filteredChargers.map((c: any) => c.id);
        const newSelection = Array.from(new Set([...selectedIds, ...filteredIds]));
        setValue('target_ids', newSelection, { shouldValidate: true, shouldDirty: true });
        setError('');
    };

    const handleDeselectAll = (event: React.MouseEvent) => {
        event.stopPropagation();
        // Deselect all filtered chargers
        const filteredIds = new Set(filteredChargers.map((c: any) => c.id));
        const newSelection = selectedIds.filter((id: string) => !filteredIds.has(id));
        setValue('target_ids', newSelection, { shouldValidate: true, shouldDirty: true });
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
    };

    const allFilteredSelected = filteredChargers.length > 0 &&
        filteredChargers.every((c: any) => selectedIds.includes(c.id));
    const someFilteredSelected = filteredChargers.some((c: any) => selectedIds.includes(c.id));

    // Validate on blur
    const handleBlur = () => {
        if (selectedIds.length === 0) {
            setError(translate('resources.firmware-updates.validation.chargers_required'));
        }
    };

    return (
        <FormControl
            fullWidth
            error={!!error}
            disabled={loading || chargers.length === 0}
        >
            <InputLabel id="target-chargers-label">
                {translate('resources.firmware-updates.fields.target_chargers')}
            </InputLabel>
            <Select
                labelId="target-chargers-label"
                multiple
                value={selectedIds}
                onChange={handleChange}
                onBlur={handleBlur}
                IconComponent={ExpandMore}
                renderValue={(selected) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            {selected.length} / {chargers.length} {translate('resources.firmware-updates.helpers.chargers_selected')}
                        </Typography>
                    </Box>
                )}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 450,
                            '& .MuiList-root': {
                                pt: 0,
                            },
                        },
                    },
                    // Keep menu open on selection
                    autoFocus: false,
                }}
                sx={{
                    '& .MuiSelect-select': {
                        py: 1.5,
                    },
                }}
            >
                {/* Sticky header with search and action buttons */}
                <Box sx={{
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid #E0E0E0',
                    zIndex: 1,
                }}>
                    {/* Search field */}
                    <Box sx={{ p: 1.5, pb: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder={translate('resources.firmware-updates.actions.search_chargers') || 'Search chargers...'}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: '#757575', fontSize: '20px' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleClearSearch();
                                            }}
                                            edge="end"
                                        >
                                            <Clear sx={{ fontSize: '18px' }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#FAFAFA',
                                },
                            }}
                        />
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        px: 1.5,
                        pb: 1.5,
                    }}>
                        <Button
                            size="small"
                            onClick={handleSelectAll}
                            disabled={allFilteredSelected || filteredChargers.length === 0}
                            startIcon={<CheckBox />}
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                borderColor: '#00C853',
                                color: '#00C853',
                                '&:hover': {
                                    backgroundColor: '#00C85310',
                                    borderColor: '#00A843',
                                },
                                '&.Mui-disabled': {
                                    borderColor: '#E0E0E0',
                                    color: '#BDBDBD',
                                },
                            }}
                            variant="outlined"
                        >
                            {translate('resources.firmware-updates.actions.select_all')}
                        </Button>
                        <Button
                            size="small"
                            onClick={handleDeselectAll}
                            disabled={!someFilteredSelected}
                            startIcon={<CheckBoxOutlineBlank />}
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                borderColor: '#757575',
                                color: '#757575',
                                '&:hover': {
                                    backgroundColor: '#75757510',
                                    borderColor: '#616161',
                                },
                                '&.Mui-disabled': {
                                    borderColor: '#E0E0E0',
                                    color: '#BDBDBD',
                                },
                            }}
                            variant="outlined"
                        >
                            {translate('resources.firmware-updates.actions.deselect_all')}
                        </Button>
                    </Box>
                </Box>

                {/* Charger items with checkboxes */}
                {filteredChargers.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center', width: '100%' }}>
                            {searchQuery
                                ? translate('resources.firmware-updates.helpers.no_chargers_found') || 'No chargers found'
                                : translate('resources.firmware-updates.helpers.no_chargers') || 'No chargers available'
                            }
                        </Typography>
                    </MenuItem>
                ) : (
                    filteredChargers.map((charger: any) => (
                    <MenuItem
                        key={charger.id}
                        value={charger.id}
                        sx={{
                            '&:hover': {
                                backgroundColor: '#00C85308',
                            },
                        }}
                    >
                        <Checkbox
                            checked={selectedIds.indexOf(charger.id) > -1}
                            sx={{
                                color: '#00C853',
                                '&.Mui-checked': {
                                    color: '#00C853',
                                },
                            }}
                        />
                        <ListItemText
                            primary={`${charger.serial_number || charger.id} - ${charger.name || 'No name'}`}
                            secondary={charger.firmware_version ? `Current: v${charger.firmware_version}` : 'No firmware version'}
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                            }}
                            secondaryTypographyProps={{
                                fontSize: '0.75rem',
                                color: charger.firmware_version ? 'text.secondary' : 'error',
                            }}
                        />
                    </MenuItem>
                    ))
                )}
            </Select>
            {error && (
                <FormHelperText error>{error}</FormHelperText>
            )}
            {!error && (
                <FormHelperText>
                    {translate('resources.firmware-updates.helpers.target_chargers')}
                </FormHelperText>
            )}
        </FormControl>
    );
};

// Separate component for site-based cascading selection
const SiteBasedSelection = ({ formData, translate, notify }: any) => {
    const [vendors, setVendors] = useState<string[]>([]);
    const [modelsByVendor, setModelsByVendor] = useState<Record<string, string[]>>({});
    const [chargers, setChargers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Step 2: Fetch vendors/models when site selected
    useEffect(() => {
        if (!formData.site_id) {
            setVendors([]);
            setModelsByVendor({});
            return;
        }

        const fetchVendorsModels = async () => {
            try {
                setLoading(true);
                const token = await tokenManager.getAccessToken();
                const response = await fetch(
                    `${API_CONFIG.baseURL}/api/backend/charger-firmware/site/${formData.site_id}/vendors-models`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (response.ok) {
                    const data = await response.json();
                    setVendors(data.vendors);
                    setModelsByVendor(data.modelsByVendor);
                }
            } catch (error) {
                console.error('Failed to load vendors/models:', error);
                notify(translate('resources.firmware-updates.notifications.failed_load_vendors_models'), { type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchVendorsModels();
    }, [formData.site_id, notify]);

    // Step 3: Fetch chargers when site, vendor, and model selected
    useEffect(() => {
        if (!formData.site_id || !formData.vendor || !formData.model) {
            setChargers([]);
            return;
        }

        const fetchChargers = async () => {
            try {
                setLoading(true);
                const token = await tokenManager.getAccessToken();
                const response = await fetch(
                    `${API_CONFIG.baseURL}/api/backend/chargers/filtered?site_id=${formData.site_id}&vendor=${formData.vendor}&model=${formData.model}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (response.ok) {
                    const data = await response.json();
                    setChargers(data);
                }
            } catch (error) {
                console.error('Failed to load chargers:', error);
                notify(translate('resources.firmware-updates.notifications.failed_load_chargers'), { type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchChargers();
    }, [formData.site_id, formData.vendor, formData.model, notify]);

    if (!formData.site_id) {
        return (
            <Alert severity="info">
                {translate('resources.firmware-updates.helpers.select_site_first')}
            </Alert>
        );
    }

    const availableModels = formData.vendor ? modelsByVendor[formData.vendor] || [] : [];

    return (
        <>
            {/* Step 2: Select Vendor */}
            <SelectInput
                source="vendor"
                label={translate('resources.firmware-updates.fields.vendor')}
                helperText={translate('resources.firmware-updates.helpers.vendor')}
                choices={vendors.map(v => ({ id: v, name: v }))}
                validate={required()}
                disabled={loading || vendors.length === 0}
                fullWidth
            />

            {vendors.length === 0 && !loading && (
                <Alert severity="warning">
                    {translate('resources.firmware-updates.helpers.no_chargers_vendor_model')}
                </Alert>
            )}

            {formData.vendor && (
                <>
                    {/* Step 3: Select Model */}
                    <SelectInput
                        source="model"
                        label={translate('resources.firmware-updates.fields.model')}
                        helperText={translate('resources.firmware-updates.helpers.model')}
                        choices={availableModels.map(m => ({ id: m, name: m }))}
                        validate={required()}
                        disabled={loading || availableModels.length === 0}
                        fullWidth
                    />

                    {availableModels.length === 0 && !loading && (
                        <Alert severity="warning">
                            {translate('resources.firmware-updates.helpers.no_models_available')}
                        </Alert>
                    )}
                </>
            )}

            {formData.vendor && formData.model && (
                <>
                    {/* Step 4: Select Chargers */}
                    <ChargerSelectionWithSelectAll
                        chargers={chargers}
                        loading={loading}
                        translate={translate}
                    />

                    {chargers.length === 0 && !loading && (
                        <Alert severity="warning">
                            {translate('resources.firmware-updates.helpers.no_chargers_combination')}
                        </Alert>
                    )}
                </>
            )}
        </>
    );
};

export default FirmwareUpdatesCreate;
